import { apiErrorMessage, handleLinkedStockDependentsError } from "components/ManageProduct/LinkedStockDependentsModal";
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Segmented,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { FC, useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import styled from '@emotion/styled';
import {
  useAdjustSkuStock,
  useDeleteSafeStock,
  useSkuStockTransactions,
  useToggleSkuStock,
  useUpsertSafeStock,
} from 'hooks/useProduct';
import { numberFormatInt } from 'utils/nuberFormat';
import { createInputNumberGuards } from 'utils';
import { backendUrl, SHOW_STOCK_ALERT } from 'config';
import { SafeProductImage } from 'components/ManageProduct/SafeProductImage';
import {
  BellOutlined,
  HistoryOutlined,
  EditOutlined,
  ExportOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useAppSelector } from 'store';
import { getProductById } from 'services/productApi';
import {
  inventorySkuFromProductByIdItem,
  mergeProductInventoryFromById,
  type InventoryListResponse,
  type InventoryProduct,
  type ProductByIdDetail,
  type ProductByIdSkuItem,
} from '../inventoryHelpers';

type ProductDetailFromApi = ProductByIdDetail;
type SkuItemFromApi = ProductByIdSkuItem;

/** ความกว้างขั้นต่ำคอลัมน์สต็อก SKU — การ์ด 3 ช่องไม่บีบจนอ่านยาก */
const SKU_STOCK_COLUMN_MIN_WIDTH = 158;

/**
 * URL รูป SKU สำหรับตารางคลัง
 * - ประกอบจาก image_id ก่อน (เหมือนการ์ดสินค้าที่ใช้ได้) เพราะ local variants ของหน้าแก้ไข/สร้าง
 *   ส่งมาแค่ image_id ไม่มี image_url
 * - fallback ไป image_url ที่ API ส่งมาเมื่อไม่มี image_id
 */
const resolveSkuImageSrc = (record: {
  image_id?: string;
  image_url?: string;
}): string | undefined => {
  if (record.image_id) return `${backendUrl}images/${record.image_id}`;
  return record.image_url || undefined;
};

/** ฟิลด์ที่ตารางคลังใช้จริง — รองรับ VariantsInput (หน้าแก้สินค้า) และ inventory API */
export interface ProductInventoryGridSku {
  id: string;
  name: string;
  image_id?: string;
  image_url?: string;
  product_code?: string;
  product_capacity?: number;
  product_quantity?: number;
  lead_time?: number;
  preparation_time?: number;
  width?: number;
  length?: number;
  height?: number;
  weight?: number;
  packaging_type_id?: string | null;
  shipping_method?: string;
  is_active?: boolean;
  is_draft?: boolean;
  is_stock?: boolean;
  /** สต็อก "ที่ขายได้" (อาจคำนวณจากส่วนประกอบที่ลิงก์) */
  stock?: {
    stock_quantity: number;
    holding_quantity: number;
    stock_balance: number;
    safe_stock_quantity?: number;
    is_safe_stock_enabled?: boolean;
  };
  /** สต็อก SKU — ยอดจริงของ SKU เอง (product_stock type=SKU) แยกจาก stock ที่ขายได้ */
  sku_stock?: {
    stock_quantity: number;
    holding_quantity: number;
    stock_balance: number;
    safe_stock_quantity?: number;
    is_safe_stock_enabled?: boolean;
  };
}

export interface ProductInventoryGridVariant {
  id?: string;
  name: string;
  capacity?: number;
  items: ProductInventoryGridSku[];
}

interface SKURow extends ProductInventoryGridSku {
  variantName: string;
  /** บริบทสินค้า — ใช้ในโหมดหลายสินค้า (products) */
  product_id?: string;
  product_name?: string;
  product_image_id?: string;
  product_image_url?: string;
  /** is_active ระดับสินค้า — ถ้า false ทุก SKU ถือว่าปิดขาย */
  product_is_active?: boolean;
}

/** กลุ่มสินค้าในโหมดตารางเดียวรวมหลายสินค้า */
export interface ProductInventoryGridProductGroup {
  id: string;
  name: string;
  /** image id (master_image) — ประกอบ URL เองด้วย backendUrl เหมือนรูป SKU */
  image_id?: string;
  /** URL เต็ม (fallback) */
  image_url?: string;
  /** is_active ระดับสินค้า — ถ้า false ทุก SKU ถือว่าปิดขาย */
  is_active?: boolean;
  variants: ProductInventoryGridVariant[];
}

interface ProductInventoryGridProps {
  /** โหมดสินค้าเดียว (หน้าแก้ไข/สร้าง) */
  variants?: ProductInventoryGridVariant[];
  productId?: string;
  /** โหมดหลายสินค้า: ตารางเดียวรวมทุกสินค้า แยกกลุ่มด้วยคอลัมน์ "สินค้า" (rowSpan) */
  products?: ProductInventoryGridProductGroup[];
  /** จาก parent: null = แสดงทุกแถว, true = เฉพาะจำกัด, false = เฉพาะไม่จำกัด */
  stockFilter?: boolean | null;
  /** ถ้ามี: เพิ่ม/แทนปุ่ม "แก้ไข" — กดแล้วเรียก callback พร้อม product ของแถวนั้น */
  onEditSku?: (skuId: string, productId?: string) => void;
  /** โหมดหลายสินค้า: กดชื่อสินค้าเพื่อดู/แก้ไขรายละเอียด */
  onViewProduct?: (productId: string) => void;
}

const ProductInventoryGrid: FC<ProductInventoryGridProps> = ({
  variants,
  productId,
  products,
  stockFilter = null,
  onEditSku,
  onViewProduct,
}) => {
  const queryClient = useQueryClient();
  const factoryId = useAppSelector((state) => state.auth.profile?.user?.role?.ref_id ?? '');
  const isMultiProduct = Array.isArray(products);
  // จำนวนสต็อกรับได้เฉพาะจำนวนเต็ม (กันพิมพ์จุดทศนิยม/ตัวอักษร)
  const integerGuards = createInputNumberGuards({ mode: 'integer' });

  /** ดึง product by id ใหม่ (เส้นเดียวกับหน้าแก้ไขสินค้า) แล้วอัปเดตเฉพาะ SKU แถวนั้นในแคช inventory — ไม่ refetch รายการทั้งโรงงาน */
  const refreshInventorySkuRowFromProductById = useCallback(
    async (skuId: string, prodId?: string) => {
      const targetProductId = prodId ?? productId;
      if (!factoryId || !targetProductId) return;
      const fresh = await queryClient.fetchQuery({
        queryKey: ['product', targetProductId],
        queryFn: () => getProductById(targetProductId),
      });
      queryClient.setQueryData(['product', targetProductId], fresh);

      const findItemInProduct = (
        product: ProductDetailFromApi | undefined,
        id: string
      ): SkuItemFromApi | null => {
        for (const v of product?.variants ?? []) {
          for (const it of v.items ?? []) {
            if (it.id === id) return it;
          }
        }
        return null;
      };

      const freshItemRaw = findItemInProduct(fresh.product, skuId);
      const freshItem = freshItemRaw ? inventorySkuFromProductByIdItem(freshItemRaw) : null;

      queryClient.setQueryData(['inventoryByFactory', factoryId], (old: unknown) => {
        if (!old || typeof old !== 'object' || !('products' in old)) return old;
        const prev = old as InventoryListResponse;
        if (!Array.isArray(prev.products)) return old;

        const products = prev.products.map((p: InventoryProduct) => {
          if (p.id !== targetProductId) return p;

          if (!freshItem) {
            return mergeProductInventoryFromById(p, fresh.product);
          }

          const variants = p.variants ?? [];
          let found = false;
          const nextVariants = variants.map((v) => {
            const items = (v.items ?? []).map((it) => {
              if (it.id !== skuId) return it;
              found = true;
              return freshItem;
            });
            return { ...v, items };
          });

          if (!found) {
            return mergeProductInventoryFromById(p, fresh.product);
          }
          return { ...p, variants: nextVariants };
        });

        return { ...prev, products };
      });
    },
    [factoryId, productId, queryClient]
  );

  const { mutateAsync: adjustApi, isPending: isAdjusting } = useAdjustSkuStock();
  const { mutateAsync: upsertSafeApi, isPending: isSavingSafe } = useUpsertSafeStock();
  const { mutateAsync: deleteSafeApi, isPending: isDeletingSafe } = useDeleteSafeStock();
  const { mutateAsync: toggleStockApi } = useToggleSkuStock();

  /** sku_id ที่กำลังสลับสถานะสต็อก — ใช้โชว์ loading เฉพาะ Switch แถวนั้น */
  const [togglingSkuId, setTogglingSkuId] = useState<string | null>(null);

  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustSku, setAdjustSku] = useState<SKURow | null>(null);
  const [adjustForm] = Form.useForm<{ direction: 'in' | 'out'; quantity: number; remark?: string }>();

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historySku, setHistorySku] = useState<SKURow | null>(null);
  const [historyPage, setHistoryPage] = useState(1);
  const historyLimit = 10;

  const [safeOpen, setSafeOpen] = useState(false);
  const [safeRow, setSafeRow] = useState<SKURow | null>(null);
  const [safeForm] = Form.useForm<{ is_active: boolean; min_quantity: number }>();

  const historyProductId = historySku?.product_id ?? productId;
  const listTxInput = useMemo(
    () => ({
      product_id: historyProductId ?? '',
      sku_id: historySku?.id ?? '',
      page: historyPage,
      limit: historyLimit,
    }),
    [historyProductId, historySku?.id, historyPage]
  );

  const { data: txRes, isFetching: txLoading } = useSkuStockTransactions(
    listTxInput,
    historyOpen && !!historyProductId && !!historySku?.id
  );

  /** flatten variants → items เป็น rows ของ table (รองรับทั้งสินค้าเดียวและหลายสินค้า) */
  const allRows: SKURow[] = useMemo(() => {
    if (isMultiProduct) {
      return (products ?? []).flatMap((product) =>
        (product.variants ?? []).flatMap((variant) =>
          (variant.items ?? []).map((item) => ({
            ...item,
            variantName: variant.name,
            product_id: product.id,
            product_name: product.name,
            product_image_id: product.image_id,
            product_image_url: product.image_url,
            product_is_active: product.is_active,
          }))
        )
      );
    }
    return (variants ?? []).flatMap((variant) =>
      (variant.items ?? []).map((item) => ({
        ...item,
        variantName: variant.name,
        product_id: productId,
      }))
    );
  }, [isMultiProduct, products, variants, productId]);

  const rows = useMemo(
    () =>
      stockFilter === null
        ? allRows
        : allRows.filter((r) => (stockFilter ? !!r.is_stock : !r.is_stock)),
    [allRows, stockFilter]
  );

  /**
   * sku id → สเปกเซลล์คอลัมน์ "สินค้า"
   * - merge ทุกแถวของสินค้าเดียวกันเป็นเซลล์เดียว: แถวแรก rowSpan = จำนวนแถวในกลุ่ม,
   *   แถวที่เหลือ = 0 (ถูก merge เข้าแถวบน)
   * - คีย์ด้วย sku id แทน index เพื่อความเสถียร (ไม่พึ่ง index จาก onCell)
   */
  const productColumnSpanById = useMemo(() => {
    const map = new Map<string, { rowSpan: number; isHeader: boolean }>();
    let i = 0;
    while (i < rows.length) {
      const pid = rows[i].product_id;
      let j = i + 1;
      while (j < rows.length && rows[j].product_id === pid) j++;
      const groupSize = j - i;
      map.set(rows[i].id, { rowSpan: groupSize, isHeader: true });
      for (let k = i + 1; k < j; k++) {
        map.set(rows[k].id, { rowSpan: 0, isHeader: false });
      }
      i = j;
    }
    return map;
  }, [rows]);

  const openAdjust = (row: SKURow) => {
    setAdjustSku(row);
    adjustForm.setFieldsValue({ direction: 'in', quantity: 1, remark: '' });
    setAdjustOpen(true);
  };

  const openHistory = (row: SKURow) => {
    setHistorySku(row);
    setHistoryPage(1);
    setHistoryOpen(true);
  };

  const submitAdjust = async () => {
    const targetProductId = adjustSku?.product_id ?? productId;
    if (!targetProductId || !adjustSku) return;
    const skuIdForTx = adjustSku.id;
    try {
      const v = await adjustForm.validateFields();
      // ส่งแค่ sku_id — backend resolve product_id เอง (product_id ที่เหลือใช้แค่ refresh cache)
      await adjustApi({
        sku_id: adjustSku.id,
        direction: v.direction,
        quantity: v.quantity,
        remark: v.remark,
      });
      message.success(v.direction === 'in' ? 'เพิ่มสต็อกเรียบร้อย' : 'ลดสต็อกเรียบร้อย');
      adjustForm.setFieldsValue({ direction: 'in', quantity: 1, remark: '' });
      await refreshInventorySkuRowFromProductById(skuIdForTx, targetProductId);
      await queryClient.invalidateQueries({ queryKey: ['skuStockTransactions', targetProductId, skuIdForTx] });
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error(err?.message || 'ไม่สามารถปรับสต็อกได้');
    }
  };

  /**
   * เปิด/ปิดใช้งานสต็อกของ SKU รายแถว
   * - เปิด: backend สร้าง product_stock (type=sku) ให้ SKU นี้ + ลิงก์คลังตัวเอง (applyOnSkuStockToggle)
   * - ปิด: ตั้ง is_stock=false (คงเรคคอร์ดสต็อกไว้ กลับมาเปิดใหม่ได้)
   */
  const handleToggleStock = async (row: SKURow, checked: boolean) => {
    const targetProductId = row.product_id ?? productId;
    if (!targetProductId) {
      message.error('ไม่พบสินค้าของ SKU นี้');
      return;
    }
    setTogglingSkuId(row.id);
    try {
      await toggleStockApi({
        sku_id: row.id,
        product_id: targetProductId,
        is_stock: checked,
      });
      message.success(checked ? 'เปิดใช้งานสต็อกแล้ว' : 'ปิดใช้งานสต็อกแล้ว');
      await refreshInventorySkuRowFromProductById(row.id, targetProductId);
    } catch (err: any) {
      if (!handleLinkedStockDependentsError(err)) {
        message.error(apiErrorMessage(err, 'ไม่สามารถเปลี่ยนสถานะสต็อกได้'));
      }
    } finally {
      setTogglingSkuId(null);
    }
  };

  const openSafeStock = (row: SKURow) => {
    setSafeRow(row);
    const st = row.stock;
    safeForm.setFieldsValue({
      is_active: st?.is_safe_stock_enabled ?? false,
      min_quantity: st?.safe_stock_quantity ?? 0,
    });
    setSafeOpen(true);
  };

  const submitSafeStock = async () => {
    const targetProductId = safeRow?.product_id ?? productId;
    if (!targetProductId || !safeRow) return;
    const skuIdForSafe = safeRow.id;
    try {
      const v = await safeForm.validateFields();
      await upsertSafeApi({
        product_id: targetProductId,
        sku_id: skuIdForSafe,
        min_quantity: v.min_quantity,
        is_active: v.is_active,
      });
      message.success('บันทึกการแจ้งเตือนสต็อกแล้ว');
      setSafeOpen(false);
      setSafeRow(null);
      await refreshInventorySkuRowFromProductById(skuIdForSafe, targetProductId);
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error(err?.message || 'บันทึกไม่สำเร็จ');
    }
  };

  const clearSafeStock = async () => {
    const targetProductId = safeRow?.product_id ?? productId;
    if (!targetProductId || !safeRow) return;
    const skuIdForSafe = safeRow.id;
    try {
      await deleteSafeApi({ product_id: targetProductId, sku_id: skuIdForSafe });
      message.success('ปิดการแจ้งเตือนสต็อกแล้ว');
      setSafeOpen(false);
      setSafeRow(null);
      await refreshInventorySkuRowFromProductById(skuIdForSafe, targetProductId);
    } catch (err: any) {
      message.error(err?.message || 'ดำเนินการไม่สำเร็จ');
    }
  };

  const productColumn = {
    title: 'สินค้า',
    key: 'product_group',
    width: '12%',
    onCell: (record: SKURow) => ({
      rowSpan: productColumnSpanById.get(record.id)?.rowSpan ?? 1,
      // ชิดบน: ข้อความสินค้จงปรับให้ าในเซลล์ที่ merge แสดงจากบนลงล่าง
      style: { verticalAlign: 'top' as const },
    }),
    render: (_: unknown, record: SKURow) => {
      // แถวแรกของกลุ่ม = แสดงข้อมูลสินค้า, แถวที่เหลือ merge รวมเป็นเซลล์ว่าง
      const isHeader = productColumnSpanById.get(record.id)?.isHeader ?? true;
      if (!isHeader) return null;
      return (
        <ProductGroupCell>
          <SKUThumb>
            <SafeProductImage
              src={resolveSkuImageSrc({
                image_id: record.product_image_id,
                image_url: record.product_image_url,
              })}
              alt={record.product_name}
              style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6 }}
            />
          </SKUThumb>
          <Typography.Link
            onClick={() => record.product_id && onViewProduct?.(record.product_id)}
            style={{
              fontSize: 13,
              fontWeight: 600,
              minWidth: 0,
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              lineHeight: 1.25,
            }}
            disabled={!onViewProduct}
          >
            {record.product_name || '-'}
            {onViewProduct && (
              <ProductLinkIconBtn
                aria-label="เปิดหน้าสินค้า"
                onClick={(e) => {
                  e.stopPropagation();
                  record.product_id && onViewProduct(record.product_id);
                }}
              >
                <ExportOutlined style={{ fontSize: 11 }} />
              </ProductLinkIconBtn>
            )}
          </Typography.Link>
        </ProductGroupCell>
      );
    },
  };

  /**
   * แถวมีค่าสต็อก SKU จริงหรือไม่ — ใช้ตัดสินว่าจะโชว์คอลัมน์ "สต็อก SKU" + ปุ่มจัดการ
   * ไม่มีค่า = ไม่จัดการสต็อก / ไม่มี sku_stock / sku_stock เป็นศูนย์ทั้งหมด
   */
  const hasSkuStockValue = (record: SKURow): boolean => {
    const s = record.sku_stock;
    if (!record.is_stock || !s) return false;
    return (
      (s.stock_quantity ?? 0) !== 0 ||
      (s.holding_quantity ?? 0) !== 0 ||
      (s.stock_balance ?? 0) !== 0 ||
      (s.safe_stock_quantity ?? 0) !== 0
    );
  };

  const columns = [
    ...(isMultiProduct ? [productColumn] : []),
    {
      title: 'สินค้า (ตัวเลือกสินค้า)',
      key: 'sku_info',
      width: isMultiProduct ? '16%' : '18%',
      render: (_: unknown, record: SKURow) => (
        <SKUInfoCell>
          <SKUThumb>
            <SafeProductImage
              src={resolveSkuImageSrc(record)}
              alt={record.name}
              style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6 }}
            />
          </SKUThumb>
          <div style={{ minWidth: 0 }}>
            <Typography.Text strong style={{ display: 'block', fontSize: 13, lineHeight: '17px' }}>
              {record.name}
            </Typography.Text>
            {record.product_code && (
              <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', lineHeight: '16px' }}>
                {record.product_code}
              </Typography.Text>
            )}
          </div>
        </SKUInfoCell>
      ),
    },
    {
      title: 'สถานะ',
      key: 'is_active',
      align: 'center' as const,
      width: '7%',
      onCell: () => ({ style: { padding: '4px 2px' } }),
      render: (_: unknown, record: SKURow) => {
        // สินค้าปิดขาย (product is_active=false) → SKU ปิดตามไปด้วย แม้ SKU is_active=true
        const effectiveActive = record.product_is_active !== false && !!record.is_active;
        return (
          <Tag color={effectiveActive ? 'green' : 'default'} style={{ borderRadius: 10, margin: 0, fontSize: 11, lineHeight: '18px', padding: '0 6px' }}>
            {effectiveActive ? 'เปิดขาย' : 'ปิดขาย'}
          </Tag>
        );
      },
    },
    {
      title: 'สถานะสต็อก',
      key: 'is_stock',
      align: 'center' as const,
      width: '9%',
      onCell: () => ({ style: { padding: '4px 2px' } }),
      render: (_: unknown, record: SKURow) => {
        const targetProductId = record.product_id ?? productId;
        return (
          <div
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}
            onClick={(e) => e.stopPropagation()}
          >
            <Switch
              size="small"
              checked={!!record.is_stock}
              loading={togglingSkuId === record.id}
              disabled={!targetProductId}
              onChange={(checked) => handleToggleStock(record, checked)}
            />
            <Typography.Text
              style={{
                fontSize: 11,
                lineHeight: '14px',
                color: record.is_stock ? '#1677ff' : '#999',
              }}
            >
              {record.is_stock ? 'ใช้งานสต็อก' : 'ไม่ได้ใช้สต็อก'}
            </Typography.Text>
          </div>
        );
      },
    },
    {
      title: 'ขนาด / น้ำหนัก',
      key: 'dimensions',
      width: '10%',
      align: 'center' as const,
      onCell: () => ({ style: { padding: '4px 0' } }),
      render: (_: unknown, record: SKURow) => {
        // ตัดทศนิยมที่ลงท้ายด้วยศูนย์ออก (13.00 → 13, 1.50 → 1.5) แต่ยังคั่นหลักพันด้วยจุลภาค
        const fmt = (n: number) =>
          Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 });
        const w = record.width ?? 0;
        const l = record.length ?? 0;
        const h = record.height ?? 0;
        const weight = record.weight ?? 0;
        const hasDim = w > 0 || l > 0 || h > 0;
        const weightLabel =
          weight >= 1000 ? `${fmt(weight / 1000)} กก.` : `${fmt(weight)} กรัม`;
        // ไม่มีทั้งขนาดและน้ำหนัก → ปล่อยว่าง
        if (!hasDim && weight <= 0) return null;
        return (
          <div style={{ textAlign: 'center', lineHeight: 1.4 }}>
            {hasDim && (
              <Typography.Text style={{ fontSize: 12 }}>
                {fmt(w)} × {fmt(l)} × {fmt(h)} ซม.
              </Typography.Text>
            )}
            {weight > 0 && (
              <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                น้ำหนัก {weightLabel}
              </Typography.Text>
            )}
          </div>
        );
      },
    },
    {
      title: 'สต็อกที่ขายได้',
      key: 'stock_info',
      align: 'center' as const,
      width: isMultiProduct ? '9%' : '10%',
      onCell: () => ({ style: { padding: '4px 6px' } }),
      render: (_: unknown, record: SKURow) => {
        if (!record.is_stock) {
          return (
            <Typography.Text type="secondary" style={{ fontSize: 12, lineHeight: '16px' }}>
              ไม่ติดตามสต็อก
            </Typography.Text>
          );
        }
        // แสดงเฉพาะจำนวน "พร้อมขาย"
        const balance = record.stock?.stock_balance ?? 0;
        return (
          <StockStatValue $tone="balance" $positive={balance > 0} style={{ fontSize: 15 }}>
            {numberFormatInt(balance)}
          </StockStatValue>
        );
      },
    },
    // ซ่อน "แจ้งเตือนสต็อก" ไว้ก่อน (SHOW_STOCK_ALERT = false)
    ...(SHOW_STOCK_ALERT ? [{
      title: 'แจ้งเตือนสต็อก',
      key: 'safe_stock',
      align: 'center' as const,
      width: isMultiProduct ? '9%' : '12%',
      onCell: () => ({ style: { padding: '4px 6px' } }),
      render: (_: unknown, record: SKURow) => {
        if (!record.is_stock) {
          return (
            <Typography.Text type="secondary" style={{ fontSize: 12, lineHeight: '16px' }}>
              —
            </Typography.Text>
          );
        }
        const st = record.stock;
        const enabled = st?.is_safe_stock_enabled ?? false;
        const minQ = st?.safe_stock_quantity ?? 0;
        const bal = st?.stock_balance ?? 0;
        const triggered = enabled && minQ > 0 && bal <= minQ;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            {enabled && minQ > 0 ? (
              <Tooltip title={`แจ้งเตือนเมื่อพร้อมขาย ≤ ${numberFormatInt(minQ)}`}>
                <Tag color={triggered ? 'error' : 'gold'} style={{ margin: 0, fontSize: 11, lineHeight: '18px', padding: '0 6px' }}>
                  <BellOutlined style={{ fontSize: 11 }} /> ≤{numberFormatInt(minQ)}
                </Tag>
              </Tooltip>
            ) : (
              <Typography.Text type="secondary" style={{ fontSize: 11, lineHeight: '16px' }}>
                ปิด
              </Typography.Text>
            )}
            <Button type="link" size="small" style={{ padding: 0, height: 20, fontSize: 11, lineHeight: 1 }} onClick={() => openSafeStock(record)}>
              ตั้งค่า
            </Button>
          </div>
        );
      },
    }] : []),
    // คอลัมน์ "การจัดการ" — ขวาสุด (เฉพาะหน้าแก้สินค้า single-product)
    ...(!isMultiProduct
      ? [{
      title: 'การจัดการ',
      key: 'actions',
      width: '9%',
      align: 'left' as const,
      // cell-actions: ไม่จางตามแถว "ไม่จำกัดสต็อก" — ปุ่มแก้ไขต้องดูกดได้เสมอ (เข้าไปตั้งค่าสต็อกได้ตลอด)
      onCell: () => ({ className: 'cell-actions', style: { padding: '4px 6px', textAlign: 'left' as const } }),
      render: (_: unknown, record: SKURow) => {
        // หน้าแก้ไขสินค้า (สินค้าเดียว): ปุ่ม "แก้ไข" อย่างเดียว
        if (onEditSku && !isMultiProduct) {
          return (
            <ActionCell>
              <Button
                type="link"
                size="small"
                icon={<EditOutlined style={{ fontSize: 13 }} />}
                disabled={!(record.product_id ?? productId)}
                onClick={() => onEditSku(record.id, record.product_id)}
                style={{ padding: 0, height: 20, fontSize: 12, lineHeight: 1 }}
              >
                แก้ไข
              </Button>
            </ActionCell>
          );
        }
        // ไม่มีค่า sku_stock (รวมถึงเป็นศูนย์ทั้งหมด) → ไม่แสดงปุ่มจัดการ
        if (!hasSkuStockValue(record)) return null;
        return (
          <ActionCell>
            <Button
              type="link"
              size="small"
              icon={<EditOutlined style={{ fontSize: 11 }} />}
              disabled={!(record.product_id ?? productId) || !record.is_stock}
              onClick={() => openAdjust(record)}
              style={{ padding: 0, height: 18, fontSize: 10, lineHeight: 1 }}
            >
              ปรับสต็อก
            </Button>
            <Button
              type="link"
              size="small"
              icon={<HistoryOutlined style={{ fontSize: 11 }} />}
              disabled={!(record.product_id ?? productId) || !record.is_stock}
              onClick={() => openHistory(record)}
              style={{ padding: 0, height: 18, fontSize: 10, lineHeight: 1 }}
            >
              ประวัติ
            </Button>
          </ActionCell>
        );
      },
    }]
      : []),
    // คอลัมน์ "แก้ไข" — ท้ายสุด (เฉพาะโหมดหลายสินค้า = ตารางคลังหน้าจัดการสินค้า)
    ...(isMultiProduct && onEditSku
      ? [{
          title: 'แก้ไข',
          key: 'edit',
          align: 'center' as const,
          width: '7%',
          fixed: 'right' as const,
          onCell: () => ({ style: { padding: '4px 6px' } }),
          render: (_: unknown, record: SKURow) => (
            <Button
              type="link"
              size="small"
              icon={<EditOutlined style={{ fontSize: 13 }} />}
              disabled={!(record.product_id ?? productId)}
              onClick={() => onEditSku(record.id, record.product_id)}
              style={{ padding: 0, height: 22, fontSize: 12, lineHeight: 1 }}
            >
              แก้ไข
            </Button>
          ),
        }]
      : []),
  ];

  const txRows = txRes?.data?.rows ?? [];
  const txTotal = txRes?.data?.total ?? 0;

  const historyColumns = [
    {
      title: 'วันที่',
      dataIndex: 'create_date',
      key: 'create_date',
      width: 160,
      render: (d: string) => (d ? dayjs(d).format('DD/MM/YYYY HH:mm') : '-'),
    },
    {
      title: 'การกระทำ',
      dataIndex: 'direction',
      key: 'direction',
      width: 90,
      render: (d: string) =>
        d === 'in' ? (
          <Tag color="blue">เข้า</Tag>
        ) : (
          <Tag color="volcano">ออก</Tag>
        ),
    },
    {
      title: 'จำนวน',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 90,
      render: (q: number) => numberFormatInt(q ?? 0),
    },
    // {
    //   title: 'ประเภท',
    //   dataIndex: 'type',
    //   key: 'type',
    //   width: 120,
    //   ellipsis: true,
    // },
    {
      title: 'หมายเหตุ',
      dataIndex: 'remark',
      key: 'remark',
      ellipsis: true,
    },
  ];

  if (rows.length === 0) {
    return (
      <EmptyWrapper>
        <Typography.Text type="secondary">ยังไม่มีรายการ SKU</Typography.Text>
      </EmptyWrapper>
    );
  }

  return (
    <Wrapper>
      <Table
        dataSource={rows}
        columns={columns}
        rowKey="id"
        pagination={false}
        size="small"
        tableLayout="fixed"
        bordered={isMultiProduct}
        rowClassName={(record) => (record.is_stock ? '' : 'row-no-stock')}
      />

      <Modal
        title={
          adjustSku ? (
            <span>
              ปรับสต็อก — <Typography.Text strong>{adjustSku.name}</Typography.Text>
            </span>
          ) : (
            'ปรับสต็อก'
          )
        }
        open={adjustOpen}
        maskClosable={!isAdjusting}
        onCancel={() => {
          if (isAdjusting) return;
          setAdjustOpen(false);
          setAdjustSku(null);
        }}
        onOk={submitAdjust}
        confirmLoading={isAdjusting}
        okText="บันทึก"
        cancelText="ปิด"
        width={520}
        destroyOnClose
      >
        <Form disabled={isAdjusting} form={adjustForm} layout="vertical" initialValues={{ direction: 'in', quantity: 1 }}>
          <Form.Item label="การทำงาน" name="direction" rules={[{ required: true }]}>
            <Segmented
              block
              options={[
                { label: 'เพิ่มเข้าคลัง', value: 'in' },
                { label: 'นำออกจากคลัง', value: 'out' },
              ]}
            />
          </Form.Item>
          <Form.Item
            label="จำนวน"
            name="quantity"
            rules={[
              { required: true, message: 'กรุณาระบุจำนวน' },
              {
                validator: (_, v) =>
                  v >= 1 ? Promise.resolve() : Promise.reject(new Error('จำนวนต้องไม่น้อยกว่า 1')),
              },
            ]}
          >
            <InputNumber
              min={1}
              precision={0}
              step={1}
              style={{ width: '100%' }}
              {...integerGuards}
            />
          </Form.Item>
          <Form.Item label="หมายเหตุ (ถ้ามี)" name="remark">
            <Input.TextArea rows={2} placeholder="เช่น รับของจากโรงงาน / ของเสียหาย / นับสต็อก" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={
          historySku ? (
            <span>
              ประวัติเข้า-ออก — <Typography.Text strong>{historySku.name}</Typography.Text>
            </span>
          ) : (
            'ประวัติเข้า-ออก'
          )
        }
        open={historyOpen}
        onCancel={() => {
          setHistoryOpen(false);
          setHistorySku(null);
        }}
        footer={null}
        width={720}
        destroyOnClose
      >
        <Table
          rowKey="id"
          size="small"
          loading={txLoading}
          columns={historyColumns}
          dataSource={txRows}
          pagination={{
            current: historyPage,
            pageSize: historyLimit,
            total: txTotal,
            showSizeChanger: false,
            onChange: (p) => setHistoryPage(p),
            showTotal: (t) => `ทั้งหมด ${t} รายการ`,
          }}
        />
      </Modal>

      <Modal
        title={
          safeRow ? (
            <span>
              แจ้งเตือนสต็อก — <Typography.Text strong>{safeRow.name}</Typography.Text>
            </span>
          ) : (
            'แจ้งเตือนสต็อก'
          )
        }
        open={safeOpen}
        maskClosable={!(isSavingSafe || isDeletingSafe)}
        onCancel={() => {
          if (isSavingSafe || isDeletingSafe) return;
          setSafeOpen(false);
          setSafeRow(null);
        }}
        destroyOnClose
        footer={[
          <Button
            key="clear"
            danger
            loading={isDeletingSafe}
            onClick={() => void clearSafeStock()}
            disabled={!safeRow || isSavingSafe}
          >
            ล้าง / ปิดการแจ้งเตือน
          </Button>,
          <Button
            key="cancel"
            disabled={isSavingSafe || isDeletingSafe}
            onClick={() => {
              setSafeOpen(false);
              setSafeRow(null);
            }}
          >
            ยกเลิก
          </Button>,
          <Button
            key="ok"
            type="primary"
            loading={isSavingSafe}
            disabled={isDeletingSafe}
            onClick={() => void submitSafeStock()}
          >
            บันทึก
          </Button>,
        ]}
      >
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 12 }}>
          เมื่อเปิดใช้ ระบบจะถือว่าควรแจ้งเตือนเมื่อจำนวน<strong>พร้อมขาย</strong>น้อยกว่าหรือเท่ากับเกณฑ์ที่กำหนด
        </Typography.Paragraph>
        <Form disabled={isSavingSafe || isDeletingSafe} form={safeForm} layout="vertical" initialValues={{ is_active: false, min_quantity: 0 }}>
          <Form.Item label="เปิดการแจ้งเตือน" name="is_active" valuePropName="checked">
            <Switch checkedChildren="เปิด" unCheckedChildren="ปิด" />
          </Form.Item>
          <Form.Item
            label="เกณฑ์ (ชิ้น) — แจ้งเมื่อพร้อมขาย ≤ ค่านี้"
            name="min_quantity"
            rules={[
              { required: true, message: 'กรุณาระบุจำนวน' },
              {
                validator: (_, v) =>
                  v >= 0 ? Promise.resolve() : Promise.reject(new Error('ต้องไม่ติดลบ')),
              },
            ]}
          >
            <InputNumber
              min={0}
              precision={0}
              step={1}
              style={{ width: '100%' }}
              placeholder="เช่น 10"
              {...integerGuards}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Wrapper>
  );
};

export default ProductInventoryGrid;

const Wrapper = styled.div`
  width: 100%;
  max-width: 100%;
  padding: 4px 0;
  background: transparent;
  overflow-x: hidden;

  .ant-table-wrapper {
    width: 100%;
    max-width: 100%;
  }

  .ant-table-content,
  .ant-table-container {
    overflow-x: hidden !important;
  }

  .ant-table table {
    width: 100% !important;
    min-width: 0 !important;
    table-layout: fixed;
  }

  .row-no-stock td:not(.cell-actions) {
    opacity: 0.65;
  }

  .ant-table {
    border-radius: 6px;
    overflow: hidden;
    font-size: 13px;
  }

  .ant-table-thead > tr > th {
    padding: 7px 8px !important;
    font-size: 12px;
    line-height: 1.3;
    font-weight: 600;
  }

  .ant-table-tbody > tr > td {
    padding: 6px 8px !important;
    line-height: 1.35;
    font-size: 13px;
  }

  .ant-table-thead > tr > th.col-sku-stock,
  .ant-table-tbody > tr > td.col-sku-stock {
    min-width: ${SKU_STOCK_COLUMN_MIN_WIDTH}px;
    padding-left: 2px !important;
    padding-right: 2px !important;
  }

  .ant-table-tbody > tr > td .ant-switch {
    transform: scale(0.98);
    transform-origin: center;
  }
`;

const SKUInfoCell = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
`;

const ProductGroupCell = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

/** ปุ่มไอคอนเปิดหน้าสินค้า — มีพื้นหลัง/สีต่างให้รู้ว่ากดได้ */
const ProductLinkIconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 6px;
  width: 18px;
  height: 18px;
  padding: 0;
  vertical-align: middle;
  border: 1px solid #91caff;
  border-radius: 4px;
  background: #e6f4ff;
  color: #1677ff;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #1677ff;
    border-color: #1677ff;
    color: #fff;
  }
`;

const SKUThumb = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;
  background: #f5f5f5;
  border: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const StockStatValue = styled.div<{ $tone: 'default' | 'hold' | 'balance'; $positive?: boolean }>`
  width: 100%;
  text-align: center;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
  font-variant-numeric: tabular-nums;
  color: ${(p) => {
    if (p.$tone === 'hold') return '#d46b08';
    if (p.$tone === 'balance') return p.$positive ? '#389e0d' : '#cf1322';
    return '#434343';
  }};
`;

const ActionCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
  width: 100%;
`;

const EmptyWrapper = styled.div`
  padding: 24px;
  text-align: center;
  background: #fff;
  border-radius: 8px;
`;
